import React, { useState, useContext } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { ArrowRight, ArrowLeft, MapPin, Calendar } from 'lucide-react';
import { ContentContext } from '../App';

const ProjectsSection = () => {
  const { language } = useLanguage();
  const content = useContext(ContentContext);
  const [activeProject, setActiveProject] = useState(0);
  const [showAfter, setShowAfter] = useState(true);

  const projects = content?.projects || [];

  if (projects.length === 0) return null;

  const nextProject = () => {
    setActiveProject((prev) => (prev + 1) % projects.length);
  };

  const prevProject = () => {
    setActiveProject((prev) => (prev - 1 + projects.length) % projects.length);
  };

  const currentProject = projects[activeProject];

  return (
    <section id="projects" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-amber-600 font-semibold tracking-wide uppercase text-sm">
            {language === 'en' ? 'Our Portfolio' : '我们的作品'}
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-2">
            {language === 'en' ? 'Our Recent Work' : '近期工程'}
          </h2>
          <p className="text-slate-600 mt-4 text-lg">
            {language === 'en' ? 'Quality Results You Can Trust' : '值得信赖的优质成果'}
          </p>
        </div>

        {/* Featured Project - Before/After */}
        <div className="grid lg:grid-cols-2 gap-8 mb-12">
          {/* Image Comparison */}
          <div className="relative rounded-2xl overflow-hidden bg-slate-100 aspect-[4/3]">
            <img
              src={showAfter ? currentProject.after_image : currentProject.before_image}
              alt={language === 'en' ? currentProject.title_en : currentProject.title_zh}
              className="w-full h-full object-cover transition-opacity duration-500"
            />
            
            {/* Before/After Toggle */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex bg-slate-900/80 backdrop-blur-sm rounded-full p-1">
              <button
                onClick={() => setShowAfter(false)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  !showAfter ? 'bg-amber-500 text-slate-900' : 'text-white hover:text-amber-400'
                }`}
              >
                {language === 'en' ? 'Before' : '之前'}
              </button>
              <button
                onClick={() => setShowAfter(true)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  showAfter ? 'bg-amber-500 text-slate-900' : 'text-white hover:text-amber-400'
                }`}
              >
                {language === 'en' ? 'After' : '之后'}
              </button>
            </div>

            {/* Label */}
            <div className="absolute top-4 left-4 bg-amber-500 text-slate-900 font-semibold px-3 py-1 rounded-lg text-sm">
              {showAfter ? (language === 'en' ? 'After' : '之后') : (language === 'en' ? 'Before' : '之前')}
            </div>
          </div>

          {/* Project Details */}
          <div className="flex flex-col justify-center">
            <h3 className="text-2xl font-bold text-slate-900 mb-4">
              {language === 'en' ? currentProject.title_en : currentProject.title_zh}
            </h3>
            
            <div className="flex items-center gap-6 text-slate-600 mb-6">
              <div className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-amber-500" />
                {language === 'en' ? currentProject.location_en : currentProject.location_zh}
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-amber-500" />
                {currentProject.date}
              </div>
            </div>

            <p className="text-slate-600 mb-8">
              {language === 'en' 
                ? 'This project showcases our commitment to quality and attention to detail. Using premium materials and expert craftsmanship, we delivered exceptional results that exceeded our client\'s expectations.'
                : '这个项目展示了我们对质量和细节的承诺。使用优质材料和专业工艺，我们交付了超越客户期望的卓越成果。'}
            </p>

            {/* Navigation */}
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="icon"
                onClick={prevProject}
                className="border-slate-300 hover:bg-slate-100"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex gap-2">
                {projects.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveProject(index)}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      index === activeProject ? 'bg-amber-500' : 'bg-slate-300'
                    }`}
                  />
                ))}
              </div>
              <Button
                variant="outline"
                size="icon"
                onClick={nextProject}
                className="border-slate-300 hover:bg-slate-100"
              >
                <ArrowRight className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>

        {/* Project Grid */}
        <div className="grid md:grid-cols-4 gap-4">
          {projects.map((project, index) => (
            <button
              key={project.id}
              onClick={() => setActiveProject(index)}
              className={`relative rounded-xl overflow-hidden aspect-square group ${
                index === activeProject ? 'ring-2 ring-amber-500' : ''
              }`}
            >
              <img
                src={project.after_image}
                alt={language === 'en' ? project.title_en : project.title_zh}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-slate-900/40 group-hover:bg-slate-900/60 transition-colors" />
              <div className="absolute bottom-3 left-3 right-3">
                <div className="text-white font-semibold text-sm">
                  {language === 'en' ? project.title_en : project.title_zh}
                </div>
                <div className="text-slate-300 text-xs">
                  {language === 'en' ? project.location_en : project.location_zh}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProjectsSection;
